import { createSlideGenerationPrompt, type SlideGenerationConfig } from '../constants/prompts';
import { 
  generateSlideTypePrompt, 
  calculateObjectCount, 
  getSlideTypeConfig, 
  createSlideUpdatePrompt, 
  createSlideRegenerationPrompt,
  createSlideTitleValidationPrompt,
  createPresentationTitlePrompt,
  generateAIContentPrompt,
  getDefaultSlideTitle,
  isGenericTitle 
} from '../constants/slidePrompts';
import { apiLog, slideLog, logger } from './logger';
import { SlideContentItem, ProcessedSlide, SlideNumberStats, SlideGroupData } from '../types';

// Gemini 2.0 Flash 가격 정보 (USD per 1M tokens)
const GEMINI_PRICING = {
  INPUT_TOKENS_PER_MILLION: 0.10,
  OUTPUT_TOKENS_PER_MILLION: 0.40
};

// 토큰 사용량을 비용으로 계산하는 함수
const calculateTokenCost = (promptTokens: number, outputTokens: number): number => {
  const inputCost = (promptTokens / 1_000_000) * GEMINI_PRICING.INPUT_TOKENS_PER_MILLION;
  const outputCost = (outputTokens / 1_000_000) * GEMINI_PRICING.OUTPUT_TOKENS_PER_MILLION;
  return inputCost + outputCost;
};

// 사용량 메타데이터를 TokenUsage로 변환하는 함수
const parseTokenUsage = (usageMetadata?: { promptTokenCount: number; candidatesTokenCount: number; totalTokenCount: number }): TokenUsage | null => {
  if (!usageMetadata) {return null;}
  
  const promptTokens = usageMetadata.promptTokenCount || 0;
  const outputTokens = usageMetadata.candidatesTokenCount || 0;
  const totalTokens = usageMetadata.totalTokenCount || (promptTokens + outputTokens);
  const estimatedCost = calculateTokenCost(promptTokens, outputTokens);
  
  return {
    promptTokens,
    outputTokens,
    totalTokens,
    estimatedCost
  };
};

export interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
  usageMetadata?: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
  error?: {
    code: number;
    message: string;
    status: string;
  };
}

export interface TokenUsage {
  promptTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number; // in USD
}

export interface ApiError {
  message: string;
  type: 'network' | 'auth' | 'quota' | 'validation' | 'server' | 'unknown';
  status?: number;
  userMessage: string;
}

const createApiError = (error: unknown, status?: number): ApiError => {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network connection failed',
      type: 'network',
      userMessage: '네트워크 연결에 실패했습니다. 인터넷 연결을 확인해주세요.'
    };
  }

  if (status === 401) {
    return {
      message: 'Invalid API key',
      type: 'auth',
      status,
      userMessage: 'API 키가 유효하지 않습니다. 설정에서 올바른 Gemini API 키를 입력해주세요.'
    };
  }

  if (status === 403) {
    return {
      message: 'API access forbidden',
      type: 'auth',
      status,
      userMessage: 'API 접근이 거부되었습니다. API 키 권한을 확인해주세요.'
    };
  }

  if (status === 429) {
    return {
      message: 'Rate limit exceeded',
      type: 'quota',
      status,
      userMessage: 'API 요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요.'
    };
  }

  if (status === 400) {
    return {
      message: 'Invalid request',
      type: 'validation',
      status,
      userMessage: '요청이 잘못되었습니다. 입력 내용을 확인해주세요.'
    };
  }

  if (status && status >= 500) {
    return {
      message: 'Server error',
      type: 'server',
      status,
      userMessage: 'AI 서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.'
    };
  }

  return {
    message: error instanceof Error ? error.message : 'Unknown error',
    type: 'unknown',
    status,
    userMessage: '알 수 없는 오류가 발생했습니다. 다시 시도해주세요.'
  };
};

export const callGeminiAPI = async (prompt: string, apiKey: string): Promise<string> => {
  if (!apiKey) {
    const error: ApiError = {
      message: 'Gemini API key is required',
      type: 'validation',
      userMessage: 'Gemini API 키가 필요합니다. 설정에서 API 키를 입력해주세요.'
    };
    throw error;
  }

  if (!prompt.trim()) {
    const error: ApiError = {
      message: 'Prompt is required',
      type: 'validation',
      userMessage: '내용을 입력해주세요.'
    };
    throw error;
  }

  let response: Response;
  
  try {
    response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': apiKey,
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192,
        }
      }),
      signal: AbortSignal.timeout(30000) // 30초 타임아웃
    });
  } catch (error) {
    throw createApiError(error);
  }

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {  // Removed unused error variable
      // console.error('Failed to parse error response as JSON:', parseError);
      // JSON 파싱 실패 시 기본 에러 처리
    }
    
    throw createApiError(errorData || new Error(`HTTP ${response.status}`), response.status);
  }

  let data: GeminiResponse;
  try {
    data = await response.json();
  } catch {  // Removed unused error variable
    // console.error('Failed to parse Gemini API response:', error);
    const apiError: ApiError = {
      message: 'Failed to parse response',
      type: 'server',
      userMessage: 'AI 응답을 처리하는 중 오류가 발생했습니다.'
    };
    throw apiError;
  }

  if (data.error) {
    throw createApiError(data.error, data.error.code);
  }
  
  if (!data.candidates || data.candidates.length === 0) {
    const error: ApiError = {
      message: 'No response from Gemini API',
      type: 'server',
      userMessage: 'AI에서 응답을 받지 못했습니다. 다시 시도해주세요.'
    };
    throw error;
  }

  const candidate = data.candidates[0];
  if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
    const error: ApiError = {
      message: 'Invalid response format',
      type: 'server',
      userMessage: 'AI 응답 형식이 올바르지 않습니다.'
    };
    throw error;
  }

  return candidate.content.parts[0].text;
};

// 토큰 사용량 정보와 함께 API 호출하는 함수
export const callGeminiAPIWithUsage = async (prompt: string, apiKey: string): Promise<{ text: string; tokenUsage: TokenUsage | null }> => {
  if (!apiKey) {
    const error: ApiError = {
      message: 'Gemini API key is required',
      type: 'validation',
      userMessage: 'Gemini API 키가 필요합니다. 설정에서 API 키를 입력해주세요.'
    };
    throw error;
  }

  if (!prompt.trim()) {
    const error: ApiError = {
      message: 'Prompt is required',
      type: 'validation',
      userMessage: '내용을 입력해주세요.'
    };
    throw error;
  }

  let response: Response;
  
  try {
    response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': apiKey,
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192,
        }
      }),
      signal: AbortSignal.timeout(30000) // 30초 타임아웃
    });
  } catch (error) {
    throw createApiError(error);
  }

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      // JSON 파싱 실패 시 기본 에러 처리
    }
    
    throw createApiError(errorData || new Error(`HTTP ${response.status}`), response.status);
  }

  let data: GeminiResponse;
  try {
    data = await response.json();
  } catch {
    const apiError: ApiError = {
      message: 'Failed to parse response',
      type: 'server',
      userMessage: 'AI 응답을 처리하는 중 오류가 발생했습니다.'
    };
    throw apiError;
  }

  if (data.error) {
    throw createApiError(data.error, data.error.code);
  }
  
  if (!data.candidates || data.candidates.length === 0) {
    const error: ApiError = {
      message: 'No response from Gemini API',
      type: 'server',
      userMessage: 'AI에서 응답을 받지 못했습니다. 다시 시도해주세요.'
    };
    throw error;
  }

  const candidate = data.candidates[0];
  if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
    const error: ApiError = {
      message: 'Invalid response format',
      type: 'server',
      userMessage: 'AI 응답 형식이 올바르지 않습니다.'
    };
    throw error;
  }

  const text = candidate.content.parts[0].text;
  const tokenUsage = parseTokenUsage(data.usageMetadata);

  return { text, tokenUsage };
};

//이하 제미나이 프롬프트 설정

// 토큰 사용량을 포함한 슬라이드 생성 함수
export const generateSlidesWithGeminiAndUsage = async (
  content: string,
  apiKey: string,
  language: 'ko' | 'en',
  slideType: 'card-news' | 'ppt' | 'image-card' = 'ppt',
  slideCount = 5
): Promise<{ slides: ProcessedSlide[]; tokenUsage: TokenUsage | null }> => {
  const config: SlideGenerationConfig = {
    language,
    slideType,
    slideCount
  };

  // 새로운 슬라이드 타입별 프롬프트 시스템 사용 (기존 함수와 동일)
  const prompt = generateSlideTypePrompt(slideType, content, language, slideCount);
  
  // 디버깅: 프롬프트 확인 (제목 관련 부분만)
  const titleRulesMatch = prompt.match(/### 제목 생성 규칙[\s\S]*?제목은.*?길이로/m);
  if (titleRulesMatch) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Title generation rules found in prompt:', titleRulesMatch[0].substring(0, 200) + '...');
    }
  }
  
  const result = await callGeminiAPIWithUsage(prompt, apiKey);

  try {
    // JSON 응답을 파싱하고 정제
    let cleanedText = result.text.replace(/```json\s*|\s*```/g, '').trim();
    
    // 디버깅: AI 응답 로그
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 AI Response (first 1000 chars):', cleanedText.substring(0, 1000));
      console.log('📊 Total response length:', cleanedText.length);
    }
    
    // JSON 응답 유효성 검사 - 잘린 응답 감지
    const lastChar = cleanedText.trim().slice(-1);
    const lastFewChars = cleanedText.trim().slice(-10);
    if (lastChar !== ']' && lastChar !== '}') {
      console.error('⚠️ Response appears to be truncated. Last 10 chars:', lastFewChars);
      console.error('📏 Response length:', cleanedText.length, 'characters');
      
      // 잘린 JSON을 복구 시도
      let fixedText = cleanedText;
      
      // 마지막 완전한 객체 찾기
      const lastCompleteObject = cleanedText.lastIndexOf('},');
      if (lastCompleteObject > 0) {
        fixedText = cleanedText.substring(0, lastCompleteObject + 1) + ']';
        console.log('🔧 Attempting to fix truncated JSON by closing at position:', lastCompleteObject);
      } else {
        throw new Error(`Response truncated at position ${cleanedText.length}. The response is too large for the current token limit.`);
      }
      
      cleanedText = fixedText;
    }
    
    // AI 응답 구조 확인
    try {
      const parsedResponse = JSON.parse(cleanedText);
      if (Array.isArray(parsedResponse) && parsedResponse.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 First AI object structure:', JSON.stringify(parsedResponse[0], null, 2));
          console.log('🔍 Total objects in response:', parsedResponse.length);
        }
        
        // slideNumber별 객체 수 확인
        const slideNumberCounts = parsedResponse.reduce((acc: SlideNumberStats, obj: SlideContentItem) => {
          const slideNum = obj.slideNumber || 'unknown';
          acc[slideNum] = (acc[slideNum] || 0) + 1;
          return acc;
        }, {});
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Objects per slideNumber:', slideNumberCounts);
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Failed to parse response for debugging:', e);
      }
    }
    
    let jsonResponse;
    try {
      jsonResponse = JSON.parse(cleanedText);
    } catch (parseError) {
      console.error('❌ JSON 파싱 실패:', parseError);
      console.error('📄 Response length:', cleanedText.length);
      console.error('🔚 Last 100 chars:', cleanedText.slice(-100));
      
      // 더 구체적인 에러 메시지
      if (cleanedText.length > 8000) {
        throw new Error(`응답이 너무 큽니다 (${cleanedText.length}자). 슬라이드 수를 줄이거나 내용을 간략하게 해주세요.`);
      }
      throw new Error(`JSON 파싱 오류: ${parseError instanceof Error ? parseError.message : '알 수 없는 오류'}`);
    }

    // 디버깅: 파싱된 데이터 구조 확인
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Parsed data type:', typeof jsonResponse);
      console.log('🔍 Is array?:', Array.isArray(jsonResponse));
    }
    
    if (!Array.isArray(jsonResponse)) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Response is not an array:', jsonResponse);
      }
      throw new Error('응답이 올바른 배열 형식이 아닙니다.');
    }

    // generateSlidesWithGemini와 동일한 방식으로 처리
    if (process.env.NODE_ENV === 'development') {
      console.log('Successfully parsed JSON response');
      console.log('JSON response array length:', jsonResponse.length);
      console.log('Requested slide count:', slideCount);
    }
    
    // 슬라이드 번호별로 그룹화
    const slideGroups = jsonResponse.reduce((acc: SlideGroupData, item: SlideContentItem) => {
      const slideNum = item.slideNumber || 1;
      if (!acc[slideNum]) {
        acc[slideNum] = [];
      }
      acc[slideNum].push(item);
      return acc;
    }, {} as SlideGroupData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Grouped slides:', slideGroups);
      console.log('Available slide numbers:', Object.keys(slideGroups));
    }
    
    // 각 슬라이드 처리
    const processedSlides = [];
    for (let i = 1; i <= slideCount; i++) {
      const slideData = slideGroups[i];
      
      if (slideData && slideData.length > 0) {
        // 제목 찾기
        const titleObj = slideData.find((item: SlideContentItem) => item.category === 'title');
        let title = '';
        
        if (titleObj && titleObj.content) {
          title = typeof titleObj.content === 'string' ? titleObj.content : '';
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Found title for slide ${i}: "${title}"`);
          }
        }
        
        // 제목이 없거나 일반적인 경우
        if (!title || title.trim() === '' || isGenericTitle(title)) {
          if (process.env.NODE_ENV === 'development') {
            console.error(`❌ Slide ${i} has no proper title or generic title: "${title}"`);
          }
          title = `[제목 생성 오류 - 슬라이드 ${i}]`;
        }
        
        processedSlides.push({
          title,
          content: '',
          structuredContent: slideData,
          keywords: []
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Created slide ${i} with title: "${title}" and ${slideData.length} objects`);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`⚠️ No data found for slide ${i}`);
        }
        processedSlides.push({
          title: `[데이터 없음 - 슬라이드 ${i}]`,
          content: '',
          structuredContent: [],
          keywords: []
        });
      }
    }
    
    // 최종 검증
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Final result:', processedSlides.map((slide, index) => 
        `Slide ${index + 1}: "${slide.title}" - ${slide.structuredContent?.length || 0} objects`
      ));
    }
    
    return { slides: processedSlides, tokenUsage: result.tokenUsage };

  } catch (parseError) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Gemini API 응답 파싱 오류:', parseError);
      console.error('원본 응답:', result.text);
    }
    
    const fallbackSlides = Array.from({ length: slideCount }, (_, i) => ({
      title: `슬라이드 ${i + 1}`,
      content: i === 0 ? content.substring(0, 200) : `${content.substring(i * 100, (i + 1) * 100)}...`,
      structuredContent: [
        { category: 'title', content: `슬라이드 ${i + 1}`, type: 'title' },
        { category: 'content', content: i === 0 ? content.substring(0, 200) : `${content.substring(i * 100, (i + 1) * 100)}...`, type: 'text' }
      ]
    }));

    return { slides: fallbackSlides, tokenUsage: result.tokenUsage };
  }
};

export const generateSlidesWithGemini = async (
  content: string,
  apiKey: string,
  language: 'ko' | 'en',
  slideType: 'card-news' | 'ppt' | 'image-card' = 'ppt',
  slideCount = 5
): Promise<ProcessedSlide[]> => {
  const config: SlideGenerationConfig = {
    language,
    slideType,
    slideCount
  };

  // 새로운 슬라이드 타입별 프롬프트 시스템 사용
  const prompt = generateSlideTypePrompt(slideType, content, language, slideCount);

  try {
    const response = await callGeminiAPI(prompt, apiKey);
    
    // JSON 응답 파싱 시도
    try {
      // JSON 응답에서 ```json이나 기타 마크다운 제거
      let cleanResponse = response.trim();
      if (cleanResponse.startsWith('```json')) {
        cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (cleanResponse.startsWith('```')) {
        cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }
      
      const jsonResponse = JSON.parse(cleanResponse);
      if (Array.isArray(jsonResponse)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Successfully parsed JSON response');
          console.log('JSON response array length:', jsonResponse.length);
          console.log('Requested slide count:', slideCount);
        
          // AI 응답 상세 디버그
          console.log('🔍 AI Response Debug - First 3 objects:');
          jsonResponse.slice(0, 3).forEach((obj, idx) => {
            console.log(`Object ${idx}:`, {
              slideNumber: obj.slideNumber,
              category: obj.category,
              content: obj.category === 'title' ? obj.content : '...',
              format: obj.format
            });
          });
        }
        
        // 객체 수 통계 확인
        const slideStats = jsonResponse.reduce((acc: Record<string, { title: number; content: number; total: number }>, item: SlideContentItem) => {
          const slideNum = item.slideNumber || 1;
          if (!acc[slideNum]) {
            acc[slideNum] = { title: 0, content: 0, total: 0 };
          }
          acc[slideNum].total++;
          if (item.category === 'title') {
            acc[slideNum].title++;
          } else if (item.category === 'content') {
            acc[slideNum].content++;
          }
          return acc;
        }, {});
        
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Slide object statistics:');
          Object.entries(slideStats).forEach(([slideNum, stats]) => {
            console.log(`  Slide ${slideNum}: ${stats.title} title + ${stats.content} content = ${stats.total} total`);
          });
          console.log('🎯 Expected: Each slide should have 1 title + 2-6 content objects (PPT type)');
        }
        
        // JSON 응답을 슬라이드 형식으로 변환
        const slides: ProcessedSlide[] = [];
        
        // 슬라이드 번호별로 그룹화
        const slideGroups = jsonResponse.reduce((acc: SlideGroupData, item: SlideContentItem) => {
          const slideNum = item.slideNumber || 1; // slideNumber가 없으면 1로 기본값
          if (!acc[slideNum]) {
            acc[slideNum] = [];
          }
          acc[slideNum].push(item);
          return acc;
        }, {} as SlideGroupData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Grouped slides:', slideGroups);
          console.log('Available slide numbers:', Object.keys(slideGroups));
        }
        
        // 객체 분배 상태 검사 및 재분배
        const availableSlideNumbers = Object.keys(slideGroups).map(Number);
        
        // PPT 타입의 경우 새로운 프롬프트에 맞게 재분배 로직 비활성화
        const needsRedistribution = slideType !== 'ppt' && (
          availableSlideNumbers.length === 1 || // 모든 객체가 한 슬라이드에 몰린 경우
          availableSlideNumbers.length < slideCount || // 요청된 슬라이드 수보다 적게 분배된 경우
          availableSlideNumbers.some(num => num > slideCount) // 슬라이드 번호가 요청 수를 초과한 경우
        );

        if (needsRedistribution) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Objects distribution is uneven, redistributing...');
            console.log('Current distribution:', availableSlideNumbers.map(num => `Slide ${num}: ${slideGroups[num]?.length || 0} objects`));
          }
          
          const allObjects = jsonResponse;
          const redistributedGroups: SlideGroupData = {};
          
          // 제목과 내용 객체를 분리
          const titleObjects = allObjects.filter(obj => obj.category === 'title');
          const contentObjects = allObjects.filter(obj => obj.category === 'content');
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Found ${titleObjects.length} title objects and ${contentObjects.length} content objects`);
          }
          
          // 제목이 부족한 경우 기본 제목 생성
          if (titleObjects.length < slideCount) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`Not enough titles (${titleObjects.length} < ${slideCount}), creating default titles...`);
            }
            
            for (let i = titleObjects.length; i < slideCount; i++) {
              // 일반적인 템플릿 대신 임시 제목 사용
              const tempTitle = language === 'ko' 
                ? `슬라이드 ${i + 1} 제목`
                : `Slide ${i + 1} Title`;
              titleObjects.push({
                category: 'title',
                format: 'short-text',
                designStyle: 'title-signboard',
                content: tempTitle,
                metadata: { backgroundColor: 'theme' }
              });
            }
          }
          
          // 슬라이드당 최소 객체 수 계산 (제목 제외)
          // slidePrompts.ts의 설정을 활용
          const typeConfig = getSlideTypeConfig(slideType);
          const contentLength = content.length; // 전체 콘텐츠 길이
          const hasMedia = contentObjects.some(obj => obj.format === 'chart' || obj.format === 'image');
          
          // calculateObjectCount 함수를 사용하여 적절한 객체 수 계산
          const { min: minObjectsPerSlide, max: maxObjectsPerSlide } = calculateObjectCount(slideType, contentLength / slideCount, hasMedia);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Slide type: ${slideType}, Content length per slide: ${Math.floor(contentLength / slideCount)}, Has media: ${hasMedia}`);
            console.log(`Object count range: ${minObjectsPerSlide}-${maxObjectsPerSlide} per slide (from slidePrompts.ts config)`);
            console.log(`Type config for '${slideType}':`, typeConfig);
          }
          
          // 각 슬라이드 초기화 및 제목 할당
          for (let i = 1; i <= slideCount; i++) {
            redistributedGroups[i] = [];
            
            // 제목 객체 할당 (있으면 하나씩, 없으면 기본 제목 생성)
            if (titleObjects.length >= i) {
              redistributedGroups[i].push({ ...titleObjects[i - 1], slideNumber: i });
            } else {
              // 기본 제목 템플릿 사용
              const defaultTitle = getDefaultSlideTitle(i - 1, language, slideType, slideCount);
              
              redistributedGroups[i].push({
                slideNumber: i,
                category: 'title',
                format: 'short-text',
                designStyle: 'title-signboard',
                content: defaultTitle,
                metadata: { backgroundColor: 'theme' }
              });
            }
          }
          
          // 내용 객체를 적절한 수로 분배 (최소 객체 수 보장)
          let contentIndex = 0;
          
          // 전체 객체가 부족한 경우, 기존 객체를 복제하여 보충
          const totalNeededObjects = slideCount * minObjectsPerSlide;
          const idealTotalObjects = slideCount * Math.floor((minObjectsPerSlide + maxObjectsPerSlide) / 2); // 이상적인 객체 수
          
          if (contentObjects.length < totalNeededObjects && contentObjects.length > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`Not enough objects (${contentObjects.length} < ${totalNeededObjects}), duplicating and modifying existing objects...`);
            }
            const originalLength = contentObjects.length;
            let duplicateIndex = 0;
            
            // 객체 유형별로 분류
            const textObjects = contentObjects.filter(obj => obj.format === 'text-list' || obj.format === 'short-text' || obj.format === 'text');
            const chartObjects = contentObjects.filter(obj => obj.format === 'chart');
            const imageObjects = contentObjects.filter(obj => obj.format === 'image');
            
            while (contentObjects.length < totalNeededObjects) {
              // 텍스트 객체가 있으면 우선적으로 복제
              if (textObjects.length > 0) {
                const objToDuplicate = textObjects[duplicateIndex % textObjects.length];
                const duplicatedObj = {
                  ...objToDuplicate,
                  format: duplicateIndex % 2 === 0 ? 'text-list' : 'info-blocks',
                  designStyle: duplicateIndex % 2 === 0 ? 'symbol-list' : 'info-card',
                  content: objToDuplicate.content && objToDuplicate.content.items ? {
                    items: objToDuplicate.content.items.map((item: { heading?: string; description?: string }, idx: number) => ({
                      ...item,
                      heading: item.heading + ` (보충 ${duplicateIndex + 1})`,
                      description: item.description || '추가 설명'
                    }))
                  } : objToDuplicate.content
                };
                contentObjects.push(duplicatedObj);
              } 
              // 차트나 이미지 객체 복제
              else if (chartObjects.length > 0 || imageObjects.length > 0) {
                const allMediaObjects = [...chartObjects, ...imageObjects];
                const objToDuplicate = allMediaObjects[duplicateIndex % allMediaObjects.length];
                contentObjects.push({ ...objToDuplicate });
              }
              // 기본 텍스트 객체 생성
              else {
                const defaultEmojis = ['📌', '🔸', '✅', '🔹', '🟢', '🟠'];
                contentObjects.push({
                  category: 'content',
                  format: duplicateIndex % 3 === 0 ? 'text-list' : duplicateIndex % 3 === 1 ? 'info-blocks' : 'short-text',
                  designStyle: duplicateIndex % 3 === 0 ? 'symbol-list' : duplicateIndex % 3 === 1 ? 'info-card' : 'content-list',
                  content: duplicateIndex % 3 === 2 ? `추가 설명 ${duplicateIndex + 1}` : {
                    items: [{
                      designElement: duplicateIndex % 2 === 0 ? '네모 div' : '카드 형태',
                      emoji: defaultEmojis[duplicateIndex % defaultEmojis.length],
                      heading: `항목 ${duplicateIndex + 1}`,
                      description: '관련 내용을 추가해주세요.'
                    }]
                  },
                  metadata: { backgroundColor: 'theme', columns: 1 }
                });
              }
              duplicateIndex++;
            }
            
            // 이상적인 객체 수에 도달할 때까지 추가 객체 생성 (선택적)
            if (contentObjects.length < idealTotalObjects && slideType === 'ppt') {
              if (process.env.NODE_ENV === 'development') {
                console.log(`Creating additional objects to reach ideal count (${contentObjects.length} < ${idealTotalObjects})...`);
              }
              while (contentObjects.length < idealTotalObjects && contentObjects.length < slideCount * maxObjectsPerSlide) {
                const additionalEmojis = ['🌟', '💡', '📊', '🎯', '🔗', '📢'];
                contentObjects.push({
                  category: 'content',
                  format: contentObjects.length % 2 === 0 ? 'short-text' : 'text-list',
                  designStyle: contentObjects.length % 2 === 0 ? 'info-card' : 'symbol-list',
                  content: contentObjects.length % 2 === 0 ? `보충 설명 ${contentObjects.length + 1}` : {
                    items: [{
                      designElement: '카드 형태',
                      emoji: additionalEmojis[contentObjects.length % additionalEmojis.length],
                      heading: `보충 항목 ${contentObjects.length + 1}`,
                      description: '추가 정보를 제공합니다.'
                    }]
                  },
                  metadata: { backgroundColor: 'theme', columns: 1 }
                });
              }
            }
          }
          
          // 객체 분배 - 각 슬라이드가 최소 객체 수를 가지도록 보장
          // 첫 번째 패스: 각 슬라이드에 최소 객체 수 배정
          for (let i = 1; i <= slideCount && contentIndex < contentObjects.length; i++) {
            for (let j = 0; j < minObjectsPerSlide && contentIndex < contentObjects.length; j++) {
              redistributedGroups[i].push({ ...contentObjects[contentIndex], slideNumber: i });
              contentIndex++;
            }
          }
          
          // 두 번째 패스: 남은 객체를 균등하게 분배
          let slideIndex = 1;
          while (contentIndex < contentObjects.length) {
            redistributedGroups[slideIndex].push({ ...contentObjects[contentIndex], slideNumber: slideIndex });
            contentIndex++;
            slideIndex = (slideIndex % slideCount) + 1;
          }
          
          // 재분배된 그룹 사용
          Object.assign(slideGroups, redistributedGroups);
          if (process.env.NODE_ENV === 'development') {
            console.log('Redistributed slides:', Object.keys(redistributedGroups).map(key => 
              `Slide ${key}: ${redistributedGroups[parseInt(key)].length} objects (${redistributedGroups[parseInt(key)].map(obj => obj.category).join(', ')})`
            ));
          }
        }
        
        // 요청된 슬라이드 개수만큼 강제로 생성
        if (process.env.NODE_ENV === 'development') {
          console.log(`\n🎯 Ensuring exactly ${slideCount} slides are created...`);
        }
        
        // 각 슬라이드 처리 (슬라이드 번호 순서대로)
        for (let i = 1; i <= slideCount; i++) {
          const slideData = slideGroups[i];
          const titleObjects = slideData?.filter((item: SlideContentItem) => item.category === 'title') || [];
          const contentObjects = slideData?.filter((item: SlideContentItem) => item.category === 'content') || [];
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Processing slide ${i}: ${slideData?.length || 0} total objects (${titleObjects.length} titles, ${contentObjects.length} content)`);
          }
          
          if (slideData && slideData.length > 0) {
            // 제목 객체 찾기 (더 유연한 방식으로)
            let titleObj = slideData.find((item: SlideContentItem) => item.category === 'title');
            
            // 제목 객체가 없으면 첫 번째 객체를 제목으로 사용하거나 기본 제목 생성
            if (!titleObj) {
              if (slideData.length > 0) {
                titleObj = slideData[0]; // 첫 번째 객체를 제목으로 사용
                if (process.env.NODE_ENV === 'development') {
                  console.log(`No title object found for slide ${i}, using first object as title:`, titleObj?.category);
                }
              } else {
                // 제목 객체가 전혀 없는 경우 임시 제목 객체 생성
                const tempTitle = language === 'ko' 
                  ? `슬라이드 ${i} 임시 제목`
                  : `Slide ${i} Temp Title`;
                
                titleObj = {
                  category: 'title',
                  content: tempTitle,
                  format: 'short-text',
                  designStyle: 'title-signboard'
                };
              }
            }
            
            // 제목 추출 (다양한 형태의 content 구조 지원)
            let slideTitle = '';
            if (titleObj?.content) {
              if (typeof titleObj.content === 'string') {
                slideTitle = titleObj.content;
                if (process.env.NODE_ENV === 'development') {
                  console.log(`✅ Found title for slide ${i}: "${slideTitle}"`);
                }
              } else if (titleObj.content.items && titleObj.content.items[0]) {
                slideTitle = titleObj.content.items[0].heading || titleObj.content.items[0].description || '';
                if (process.env.NODE_ENV === 'development') {
                  console.log(`✅ Found complex title for slide ${i}: "${slideTitle}"`);
                }
              }
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log(`❌ No title content found for slide ${i}`);
              }
            }
            
            // 제목이 없거나 금지된 패턴인 경우 검증
            if (!slideTitle || slideTitle.trim() === '') {
              // 임시 제목 사용 (일반적인 템플릿 대신)
              slideTitle = language === 'ko' 
                ? `슬라이드 ${i}의 제목을 생성해주세요`
                : `Please generate title for slide ${i}`;
            }
            
            // 일반적인 제목인지 확인 (개요, 핵심 내용 등)
            if (slideTitle && isGenericTitle && isGenericTitle(slideTitle)) {
              if (process.env.NODE_ENV === 'development') {
                console.log(`⚠️ Slide ${i} has generic title: "${slideTitle}", keeping it temporarily`);
              }
              // 일반적인 제목이라도 일단 유지 (나중에 재생성 가능)
              // AI가 더 나은 제목을 생성하도록 프롬프트 개선이 필요
            }
            
            slides.push({
              title: slideTitle,
              content: '', // 구조화된 콘텐츠는 structuredContent에 저장
              structuredContent: slideData // 전체 구조화된 데이터 저장
            });
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Created slide ${i} with title: "${slideTitle}" and ${slideData.length} objects`);
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log(`⚠️ No data found for slide ${i}, creating placeholder slide`);
            }
            // 데이터가 없으면 기본 슬라이드 생성
            slides.push({
              title: `슬라이드 ${i}`,
              content: '내용을 추가해주세요.',
              structuredContent: []
            });
          }
        }
        
        // 최종 검증: 슬라이드 개수가 정확히 요청한 개수인지 확인
        if (slides.length !== slideCount) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`⚠️ Warning: Created ${slides.length} slides but ${slideCount} were requested`);
          }
          
          // 부족한 슬라이드 추가
          while (slides.length < slideCount) {
            const slideIndex = slides.length;
            const defaultTitle = getDefaultSlideTitle(slideIndex, language, slideType, slideCount);
            slides.push({
              title: defaultTitle,
              content: '내용을 추가해주세요.',
              structuredContent: [
                {
                  slideNumber: slideIndex + 1,
                  category: 'title',
                  format: 'short-text',
                  designStyle: 'title-signboard',
                  content: defaultTitle
                },
                {
                  slideNumber: slideIndex + 1,
                  category: 'content',
                  format: 'short-text',
                  designStyle: 'info-card',
                  content: '내용을 추가해주세요.'
                }
              ]
            });
          }
          
          // 초과한 슬라이드 제거
          if (slides.length > slideCount) {
            slides.splice(slideCount);
          }
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Final result:', slides.map((slide, index) => 
            `Slide ${index + 1}: "${slide.title}" - ${slide.structuredContent?.length || 0} objects`
          ));
          console.log(`📊 Total: ${slides.length} slides (requested: ${slideCount}), ${slides.reduce((total, slide) => total + (slide.structuredContent?.length || 0), 0)} total objects`);
        }
        return slides;
      }
    } catch (jsonError) {
      // 개발용 테스트 데이터로 폴백
      if (process.env.NODE_ENV === 'development') {
        console.log('JSON parsing failed, using test data for development');
      }
      
      // 테스트용 구조화된 데이터 (요청된 슬라이드 수만큼 생성)
      const testStructuredData = [];
      
      for (let i = 1; i <= slideCount; i++) {
        const slideTemplates = [
          {
            title: "AI 기술의 최신 동향",
            structuredContent: [
              {
                slideNumber: i,
                category: "title",
                format: "short-text",
                designStyle: "signboard",
                content: "AI 기술의 최신 동향"
              },
              {
                slideNumber: i,
                category: "content",
                format: "list",
                designStyle: "symbol-list",
                content: ["생성형 AI의 급속한 발전", "자율주행 기술의 상용화", "의료 AI 진단 시스템"],
                emoji: "🚀",
                metadata: { columns: 1 }
              }
            ]
          },
          {
            title: "핵심 기술 영역",
            structuredContent: [
              {
                slideNumber: i,
                category: "title",
                format: "short-text",
                designStyle: "signboard",
                content: "핵심 기술 영역"
              },
              {
                slideNumber: i,
                category: "content",
                format: "column-keywords",
                designStyle: "column-blocks",
                content: ["머신러닝", "딥러닝", "자연어처리"],
                emoji: "🔬",
                metadata: { columns: 3 }
              }
            ]
          },
          {
            title: "실제 적용 사례",
            structuredContent: [
              {
                slideNumber: i,
                category: "title",
                format: "short-text",
                designStyle: "signboard",
                content: "실제 적용 사례"
              },
              {
                slideNumber: i,
                category: "content",
                format: "numbered-list",
                designStyle: "numbered-list",
                content: ["자연어 처리 기반 챗봇 개발", "컴퓨터 비전을 활용한 의료 진단", "추천 시스템 개발"],
                emoji: "🏢",
                metadata: { columns: 1 }
              }
            ]
          },
          {
            title: "차트 데이터 분석",
            structuredContent: [
              {
                slideNumber: i,
                category: "title",
                format: "short-text",
                designStyle: "signboard",
                content: "차트 데이터 분석"
              },
              {
                slideNumber: i,
                category: "content",
                format: "chart",
                designStyle: "chart",
                content: {
                  type: "bar",
                  title: "AI 기술 적용 분야",
                  data: {
                    labels: ["의료", "금융", "제조", "교육"],
                    values: [30, 25, 20, 25]
                  }
                },
                emoji: "📊",
                metadata: { columns: 1 }
              }
            ]
          },
          {
            title: "미래 전망",
            structuredContent: [
              {
                slideNumber: i,
                category: "title",
                format: "short-text",
                designStyle: "signboard",
                content: "미래 전망"
              },
              {
                slideNumber: i,
                category: "content",
                format: "info-blocks",
                designStyle: "info-card",
                content: ["2025년까지 전 산업 AI 도입", "일자리 변화와 새로운 기회", "인간-AI 협업 시대 개막"],
                emoji: "🔮",
                metadata: { columns: 1 }
              }
            ]
          }
        ];
        
        const templateIndex = (i - 1) % slideTemplates.length;
        const template = slideTemplates[templateIndex];
        
        testStructuredData.push({
          title: template.title,
          content: "",
          structuredContent: template.structuredContent
        });
      }
      
      return testStructuredData;
    }
    
    // 기존 텍스트 파싱 방식
    const slides: Array<{ title: string; content: string; subtitle?: string; bulletPoints?: string[] }> = [];
    
    // Split by slide markers
    const slideBlocks = response.split('===SLIDE_START===').filter(block => block.trim());
    
    for (const block of slideBlocks) {
      const endIndex = block.indexOf('===SLIDE_END===');
      const slideContent = endIndex !== -1 ? block.substring(0, endIndex) : block;
      
      const lines = slideContent.split('\n').filter(line => line.trim());
      let title = '';
      let content = '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('TITLE:')) {
          title = trimmedLine.replace('TITLE:', '').trim();
        } else if (trimmedLine.startsWith('CONTENT:')) {
          content = trimmedLine.replace('CONTENT:', '').trim();
        }
      }
      
      if (title && content) {
        slides.push({ title, content });
      }
    }
    
    // Parsed slides logging removed for security
    
    // If parsing failed, try alternative parsing
    if (slides.length === 0) {
      // Primary parsing failed, trying alternative parsing...
      
      // Try to extract any title/content patterns
      const titleMatches = response.match(/(?:제목|TITLE|Title):\s*(.+)/gi);
      const contentMatches = response.match(/(?:내용|CONTENT|Content):\s*(.+)/gi);
      
      if (titleMatches && contentMatches) {
        const minLength = Math.min(titleMatches.length, contentMatches.length);
        for (let i = 0; i < minLength; i++) {
          const title = titleMatches[i].replace(/(?:제목|TITLE|Title):\s*/i, '').trim();
          const content = contentMatches[i].replace(/(?:내용|CONTENT|Content):\s*/i, '').trim();
          if (title && content) {
            slides.push({ title, content });
          }
        }
      }
    }
    
    // If still no slides, create a fallback slide
    if (slides.length === 0) {
      // All parsing failed, creating fallback slide...
      
      // Split content into paragraphs and create slides
      const paragraphs = content.split('\n\n').filter(p => p.trim());
      
      if (paragraphs.length > 1) {
        paragraphs.forEach((paragraph, index) => {
          const lines = paragraph.split('\n').filter(l => l.trim());
          let title = lines[0] || '';
          
          // 제목이 없거나 유효하지 않은 경우 기본 템플릿 사용
          if (!title || title === `슬라이드 ${index + 1}` || title === `Slide ${index + 1}`) {
            title = getDefaultSlideTitle(index, language, slideType, slideCount);
          }
          
          const slideContent = lines.slice(1).join(' ') || paragraph;
          
          slides.push({
            title: title.length > 50 ? title.substring(0, 50) + '...' : title,
            content: slideContent
          });
        });
      } else {
        // Single slide fallback
        slides.push({
          title: language === 'ko' ? '프레젠테이션' : 'Presentation',
          content: content
        });
      }
    }
    
    // 최종 검증: 슬라이드 개수가 정확히 요청한 개수인지 확인 (텍스트 파싱의 경우)
    if (slides.length !== slideCount) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ Text parsing: Created ${slides.length} slides but ${slideCount} were requested`);
      }
      
      // 부족한 슬라이드 추가
      while (slides.length < slideCount) {
        const slideIndex = slides.length;
        slides.push({
          title: getDefaultSlideTitle(slideIndex, language, slideType, slideCount),
          content: language === 'ko' ? '내용을 추가해주세요.' : 'Please add content.'
        });
      }
      
      // 초과한 슬라이드 제거
      if (slides.length > slideCount) {
        slides.splice(slideCount);
      }
    }
    
    return slides;
    
  } catch (error) {
    // console.error('Failed to generate slides from content:', error);
    
    // ApiError인 경우 사용자에게 친화적인 메시지와 함께 재throw
    if (error && typeof error === 'object' && 'userMessage' in error) {
      throw error;
    }
    
    // 예상치 못한 에러인 경우 일반적인 ApiError로 변환
    const apiError: ApiError = {
      message: error instanceof Error ? error.message : 'Unknown error during slide generation',
      type: 'unknown',
      userMessage: '슬라이드 생성 중 오류가 발생했습니다. 다시 시도해주세요.'
    };
    throw apiError;
  }
};

export const updateSlideWithGemini = async (
  currentTitle: string,
  currentContent: string,
  updateRequest: string,
  apiKey: string,
  language: 'ko' | 'en'
): Promise<{ title: string; content: string; subtitle?: string; bulletPoints?: string[] }> => {
  const prompt = createSlideUpdatePrompt(currentTitle, currentContent, updateRequest, language);

  try {
    const response = await callGeminiAPI(prompt, apiKey);
    
    // Parse the response
    const lines = response.split('\n').filter(line => line.trim());
    let title = currentTitle;
    let content = currentContent;
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('TITLE:')) {
        title = trimmedLine.replace('TITLE:', '').trim() || title;
      } else if (trimmedLine.startsWith('CONTENT:')) {
        content = trimmedLine.replace('CONTENT:', '').trim() || content;
      }
    }
    
    return { title, content };
    
  } catch (error) {
    // console.error('Failed to update slide content:', error);
    
    // ApiError인 경우 재throw하여 사용자에게 적절한 피드백 제공
    if (error && typeof error === 'object' && 'userMessage' in error) {
      throw error;
    }
    
    // 예상치 못한 에러인 경우 일반적인 ApiError로 변환
    const apiError: ApiError = {
      message: error instanceof Error ? error.message : 'Unknown error during slide update',
      type: 'unknown',
      userMessage: '슬라이드 수정 중 오류가 발생했습니다. 다시 시도해주세요.'
    };
    throw apiError;
  }
};

// 새로운 슬라이드 재생성 함수 (객체 유형 지정 가능)
export const regenerateSlideContentWithType = async (
  slideTitle: string,
  currentContent: string,
  updateRequest: string,
  apiKey: string,
  language: 'ko' | 'en',
  objectType?: 'text' | 'image' | 'chart'
): Promise<SlideContentItem[]> => {
  const prompt = createSlideRegenerationPrompt(
    slideTitle,
    currentContent,
    updateRequest,
    language,
    objectType
  );

  try {
    const response = await callGeminiAPI(prompt, apiKey);
    
    // JSON 응답 파싱
    let cleanResponse = response.trim();
    if (cleanResponse.startsWith('```json')) {
      cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanResponse.startsWith('```')) {
      cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    
    const jsonResponse = JSON.parse(cleanResponse);
    if (Array.isArray(jsonResponse)) {
      return jsonResponse;
    }
    
    throw new Error('응답이 배열 형식이 아닙니다.');
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to regenerate slide content with type:', error);
    }
    
    // ApiError인 경우 재throw
    if (error && typeof error === 'object' && 'userMessage' in error) {
      throw error;
    }
    
    // 예상치 못한 에러인 경우 일반적인 ApiError로 변환
    const apiError: ApiError = {
      message: error instanceof Error ? error.message : 'Unknown error during slide regeneration',
      type: 'unknown',
      userMessage: '슬라이드 재생성 중 오류가 발생했습니다. 다시 시도해주세요.'
    };
    throw apiError;
  }
};